import type { CheckoutDetailResponse, CheckoutVehicleInfo } from './dto.inventory';

/*
|--------------------------------------------------------------------------
| TRANSFORMED CHECKOUT DETAIL TYPES
|--------------------------------------------------------------------------
*/

export type TransformedVehicleOption = {
  label: string;
  name: string;
  code: string;
  type: string;
  value: number;
  description?: string;
};

export type TransformedFeature = {
  name: string;
  code: string;
  type: string;
  value: number;
  description?: string;
};

export type PricingSummaryItem = {
  label: string;
  value: number;
};

export type TransformedCheckoutDetail = {
  // Vehicle identification
  vin: string;
  vinBatchNumber?: string;
  expirationDate?: string;
  sharedDate?: string;
  shortcode: string;
  vehicleImage: string;

  // Vehicle details
  model: string;
  modelYear: string;
  trim: string;
  type: string;
  availability: string;

  // Currency
  currencyCode: string;

  // Vehicle options from vehicleInfo (excludes 'features' label)
  vehicleOptions: TransformedVehicleOption[];

  // Features from vehicleInfo 'features' content
  features: TransformedFeature[];

  // Pricing breakdown
  pricing: {
    basePrice: number;
    addOns: number;
    subtotal: number;
    configurationSubtotal?: number;
    fees: PricingSummaryItem[];
    credits: PricingSummaryItem[];
    deposit: number;
    total: number;
    taxRate: number;
  };

  // Optional quote-specific fields
  summary?: {
    prospectType: string;
    preferredPaymentMethod: string;
    deliveryTimeline: string;
    companyName: string;
    companyTaxIdentification: string;
    invoiceAddressLine1: string;
    invoiceCity: string;
    invoiceCountry: string;
    invoicePostalCode: string;
    cashDiscount?: string;
    percentageDiscount?: string;
    requestedIncentives?: string[];
    // D&B business entity data (for edit mode)
    duns?: {
      dunsNumber?: string;
      primaryName?: string;
      companyName?: string;
      isOutOfBusiness?: boolean;
    };
    agencyDiscountPercentageCap?: string;
    agencyCashDiscountCap?: string;
    agencyExtraCashDiscount?: string;
    // Finance provider details
    financeCompanyName?: string;
    financeCompanyAddress?: string;
    financeCity?: string;
    financeCountry?: string;
    financePostalCode?: string;
    financeVat?: string;
    // D&B finance entity data (for edit mode)
    financeDuns?: {
      dunsNumber?: string;
      primaryName?: string;
      companyName?: string;
      isOutOfBusiness?: boolean;
    };
    // Quotation details (for Loan/Leasing payment methods)
    uploadBankQuotation?: string;
    quoteId?: string;
    agentSubventionAmount?: string;
    monthlyPaymentInclVat?: string;
    downpayment?: string;
  };
  docusignStatus?: string;
  currentStatus?: string;
  quoteFilesMetadata?: {
    fileId: string;
    fileNameOriginal: string;
    mimeType: string;
    documentCategory?: string;
    createdAt?: string;
  }[];
};

/*
|--------------------------------------------------------------------------
| HELPER FUNCTIONS
|--------------------------------------------------------------------------
*/

/**
 * Transforms vehicleInfo items to vehicle options (excludes 'features' and 'model' labels)
 * Uses unitPriceBeforeTax from optionsInformation when available
 * @param vehicleInfo - Array of vehicle info items from checkout data
 * @param optionsInformation - Optional pricing information for options
 * @returns Array of transformed vehicle options
 */
const transformVehicleOptions = (
  vehicleInfo: CheckoutVehicleInfo[] | undefined,
  optionsInformation?: { productName: string; value: number; unitPriceBeforeTax?: number }[]
): TransformedVehicleOption[] => {
  if (!vehicleInfo) return [];

  // Create a map of option names to their unitPriceBeforeTax
  const priceMap = new Map<string, number>();
  optionsInformation?.forEach((option) => {
    if (option.unitPriceBeforeTax !== undefined) {
      priceMap.set(option.productName, option.unitPriceBeforeTax);
    }
  });

  return vehicleInfo
    .filter((item) => item.label !== 'features' && item.label !== 'model' && item.label)
    .map((item) => ({
      label: item.label || '',
      name: item.name || '',
      code: item.code || '',
      type: item.type || '',
      value: priceMap.get(item.name || '') ?? item.value ?? 0,
      description: item.description,
    }));
};

/**
 * Extracts features from the vehicleInfo 'features' item
 * Uses unitPriceBeforeTax from optionsInformation when available
 * @param vehicleInfo - Array of vehicle info items from checkout data
 * @param optionsInformation - Optional pricing information for features
 * @returns Array of transformed features
 */
const extractFeatures = (
  vehicleInfo: CheckoutVehicleInfo[] | undefined,
  optionsInformation?: { productName: string; value: number; unitPriceBeforeTax?: number }[]
): TransformedFeature[] => {
  if (!vehicleInfo) return [];

  const featuresItem = vehicleInfo.find((item) => item.label === 'features');
  if (!featuresItem?.contents) return [];

  // Create a map of feature names to their unitPriceBeforeTax
  const priceMap = new Map<string, number>();
  optionsInformation?.forEach((option) => {
    if (option.unitPriceBeforeTax !== undefined) {
      priceMap.set(option.productName, option.unitPriceBeforeTax);
    }
  });

  return featuresItem.contents.map((feature) => ({
    name: feature.name || '',
    code: feature.code || '',
    type: feature.type || '',
    value: priceMap.get(feature.name || '') ?? feature.value ?? 0,
    description: feature.description,
  }));
};

/**
 * Extracts the base price (variant value) from vehicle info array
 * @param vehicleInfo - Array of vehicle info items from checkout data
 * @returns The base price value or 0 if not found
 */
const extractBasePrice = (vehicleInfo: CheckoutVehicleInfo[] | undefined): number => {
  const variantItem = vehicleInfo?.find((item) => item.label === 'variant');
  return variantItem?.value ?? 0;
};

/*
|--------------------------------------------------------------------------
| TRANSFORMER FUNCTION
|--------------------------------------------------------------------------
*/

/**
 * Transforms the checkout detail API response to a component-friendly format
 * Uses primarily checkoutData which contains all needed information
 * @param response - The raw checkout detail API response
 * @returns Transformed checkout detail object
 * @example
 * ```typescript
 * const transformed = transformCheckoutDetail(apiResponse);
 * console.log(transformed.model); // 'air'
 * console.log(transformed.vehicleOptions[0].name); // 'Touring'
 * ```
 */
export const transformCheckoutDetail = (
  response: CheckoutDetailResponse
): TransformedCheckoutDetail => {
  const { checkoutData } = response;

  const shortcode = checkoutData?.shortcode || '';
  const pricing = checkoutData?.pricing;

  // Use vehicle image URL from API response
  const vehicleImage = response.vehicleDetailsData?.imageUrl || '';

  // Extract pricing values
  const basePrice = extractBasePrice(checkoutData?.vehicleInfo);
  const addOns = pricing?.addOns?.value ?? 0;
  const subtotal = pricing?.subtotal?.value ?? 0;
  const configurationSubtotal = pricing?.configurationSubtotal?.value;
  const deposit = pricing?.deposit?.value ?? 0;
  const total = pricing?.total?.value ?? 0;
  const taxRate = pricing?.tax?.rate ?? 0;

  // Extract fees and credits
  const fees: PricingSummaryItem[] =
    pricing?.fees
      ?.filter((fee) => fee.name !== 'Deposit Due Today')
      ?.map((fee) => ({
        label: fee.name,
        value: fee.value,
      })) ?? [];

  const credits: PricingSummaryItem[] =
    pricing?.credits
      ?.filter((credit) => credit.name !== 'RESERVATION_PAYMENT')
      ?.map((credit) => ({
        label: credit.name,
        value: credit.value,
      })) ?? [];

  return {
    // Vehicle identification
    vin: checkoutData?.vin || '',
    shortcode,
    vehicleImage,

    // Vehicle details
    model: checkoutData?.model || '',
    modelYear: checkoutData?.modelYear || '',
    trim: checkoutData?.trim || '',
    type: checkoutData?.type || '',
    availability: checkoutData?.availability || '',

    // Currency
    currencyCode: pricing?.currencyCode || 'USD',

    // Vehicle options and features
    vehicleOptions: transformVehicleOptions(
      checkoutData?.vehicleInfo,
      pricing?.tax?.optionsInformation
    ),
    features: extractFeatures(checkoutData?.vehicleInfo, pricing?.tax?.optionsInformation),

    // Pricing breakdown
    pricing: {
      basePrice,
      addOns,
      subtotal,
      configurationSubtotal,
      fees,
      credits,
      deposit,
      total,
      taxRate,
    },
  };
};
